import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, studentPhoto } from "@/lib/api";
import { Card, EmptyState } from "@/components/common";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { BellRing, Loader2, UserPlus } from "lucide-react";
import { fmtDate } from "@/lib/dates";

const tone = (d) => (d >= 10 ? "text-red-600" : d >= 5 ? "text-amber-600" : "text-zinc-500");
const since = (iso) => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`;
};

export const AwaitingDecisions = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => api.get("/awaiting-decisions").then(({ data }) => setRows(data)).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);

  const nudge = async (r) => {
    setBusy(r.pr_id);
    try {
      const { data } = await api.post(`/placement-requests/${r.pr_id}/nudge-dor`);
      toast.success(`Reminder sent to ${data.emailed.join(", ")}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  if (!rows) return null;
  const stuck = rows.filter((r) => r.days_waiting >= 5).length;

  return (
    <Card title="Awaiting DOR Yes / No" data-testid="awaiting-decisions-card"
      action={<span className="text-xs text-zinc-500" data-testid="awaiting-decisions-count">{rows.length} waiting{stuck ? ` · ${stuck} over 5 days` : ""}</span>}>
      {rows.length === 0 ? <EmptyState text="Nothing is sitting with a facility right now." /> : (
        <div className="overflow-x-auto cc-scroll -m-5">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-500 text-xs uppercase">
              <tr>
                <th className="text-left px-5 py-3">Student</th>
                <th className="text-left px-5 py-3">Facility / DOR</th>
                <th className="text-left px-5 py-3">Starts</th>
                <th className="text-left px-5 py-3">Waiting</th>
                <th className="text-left px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.pr_id} className="border-t align-top" data-testid={`awaiting-${r.pr_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar src={studentPhoto(r.student_id)} name={r.student_name} size={28} />
                      <div>
                        <div className="font-medium text-zinc-800">{r.student_name}</div>
                        <div className="text-xs text-zinc-400">{[r.discipline, r.school_name].filter(Boolean).join(" · ")}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="text-zinc-700">{r.facility_name}{r.facility_state ? ` · ${r.facility_state}` : ""}</div>
                    {r.dor_email || r.has_dor_account
                      ? <div className="text-xs text-zinc-400">{[r.dor_name, r.dor_email].filter(Boolean).join(" · ") || "DOR account on file"}</div>
                      : <div className="text-xs text-red-600 font-medium" data-testid={`awaiting-no-dor-${r.pr_id}`}>No DOR on file</div>}
                  </td>
                  <td className="px-5 py-3 text-xs font-mono text-zinc-600">{fmtDate(r.requested_start_date)}</td>
                  <td className="px-5 py-3">
                    <span className={`font-semibold ${tone(r.days_waiting)}`}>{r.days_waiting} day{r.days_waiting === 1 ? "" : "s"}</span>
                    {r.last_nudged_at && <div className="text-xs text-zinc-400">nudged {since(r.last_nudged_at)}</div>}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap gap-2">
                      {r.dor_email || r.has_dor_account ? (
                        <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busy === r.pr_id}
                          onClick={() => nudge(r)} data-testid={`nudge-dor-${r.pr_id}`}>
                          {busy === r.pr_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><BellRing className="w-3 h-3 mr-1" />Nudge DOR</>}
                        </Button>
                      ) : (
                        <Button size="sm" className="h-7 text-xs bg-cc-orange text-white" onClick={() => navigate("/facilities")}
                          data-testid={`add-dor-${r.pr_id}`}><UserPlus className="w-3 h-3 mr-1" />Add a DOR</Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => navigate(`/placements/${r.pr_id}`)}
                        data-testid={`awaiting-open-${r.pr_id}`}>Open</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};
